/**
 * ChatHeader — thread metadata row above the messages list.
 *
 * Phase 1b scope: thread chip + title + started-by line + timestamp + project-name badge.
 * Presence pod and action buttons are wired as hidden slots for Phase 4+.
 */

import React from "react";

interface ChatHeaderProps {
	title: string;
	startedByName?: string;
	/** Pre-rendered avatar node — keeps ChatHeader avatar-component-agnostic. */
	startedByAvatar?: React.ReactNode;
	createdAt?: Date;
	/** Muted suffix shown after the title, e.g. "Northwind Regulatory Platform". */
	projectName?: string;
	/** Thread message count chip. */
	turns?: number;
	/** Shows a "shared" accent chip when the session is team-visible. */
	isShared?: boolean;

	/** Presence pod — Phase 1b: hidden. Phase 4: wired via real-time hook. */
	showPresence?: boolean;
	presence?: {
		activeViewers?: Array<{ id: string; name: string; avatar?: React.ReactNode }>;
		typingUsers?: Array<{ id: string; name: string }>;
	};

	/** Action slot — Phase 1b: hidden. Phase 2+: pin / share / download / overflow. */
	showActions?: boolean;
	actions?: React.ReactNode;
}

/** Formats a Date as "9 Apr" or "9 Apr, 14:32" depending on whether the date is today. */
function formatHeaderDate(date: Date): string {
	const today = new Date();
	const isToday =
		date.getDate() === today.getDate() &&
		date.getMonth() === today.getMonth() &&
		date.getFullYear() === today.getFullYear();

	if (isToday) {
		return date.toLocaleString("en-GB", {
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: "Europe/London",
		});
	}

	return date.toLocaleString("en-GB", {
		day: "numeric",
		month: "short",
		timeZone: "Europe/London",
	});
}

/**
 * Thread metadata header rendered above the chat message list.
 *
 * Presence pod (showPresence) and action buttons (showActions) are hidden in
 * Phase 1b — their slots are wired but return null until Phase 4 enables them.
 */
export default function ChatHeader({
	title,
	startedByName,
	startedByAvatar,
	createdAt,
	projectName,
	turns,
	isShared = false,
	showPresence = false,
	showActions = false,
	actions,
}: ChatHeaderProps) {
	return (
		<header className="chat-header" aria-label={`Chat: ${title}`}>
			<div className="chat-header-meta">
				{/* Thread chip — turn count */}
				{turns !== undefined && turns > 0 && (
					<span className="chat-header-turns" aria-label={`${turns} messages`}>
						{turns}
					</span>
				)}

				{/* Session title */}
				<h2 className="chat-header-title">
					{title}
					{/* Muted project-name suffix */}
					{projectName && (
						<span className="chat-header-project-suffix" aria-label={`Project: ${projectName}`}>
							{projectName}
						</span>
					)}
				</h2>

				{/* "Shared" accent chip — pure read from existing session data */}
				{isShared && (
					<span className="chat-header-shared-chip" aria-label="Shared session">
						shared
					</span>
				)}
			</div>

			{/* Started-by line */}
			{(startedByName ?? createdAt) && (
				<p className="chat-header-byline">
					{startedByAvatar && (
						<span className="chat-header-byline-avatar" aria-hidden="true">
							{startedByAvatar}
						</span>
					)}
					{startedByName && (
						<span className="chat-header-byline-name">{startedByName}</span>
					)}
					{createdAt && (
						<time
							className="chat-header-byline-time"
							dateTime={createdAt.toISOString()}
						>
							{formatHeaderDate(createdAt)}
						</time>
					)}
				</p>
			)}

			{/* Presence pod — Phase 1b: hidden slot */}
			{showPresence && (
				<div className="chat-header-presence" aria-label="Active viewers">
					{/* Phase 4: presence avatars rendered here */}
				</div>
			)}

			{/* Action buttons slot — Phase 1b: hidden */}
			{showActions && actions && (
				<div className="chat-header-actions">
					{actions}
				</div>
			)}
		</header>
	);
}
